import { useState, useMemo } from "react";
import { Search, SlidersHorizontal, Package } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { products, manufacturers, categories, models } from "@/data/products";
import { useLanguage } from "@/i18n/LanguageContext";
import { routes } from "@/i18n/routes";
import SeoMetadata from "@/components/SeoMetadata";

const categoryTranslationKeys: Record<string, string> = {
  "Sterowniki silnika": "catalog.categoryEngineECU",
  "Sterowniki skrzyni biegów": "catalog.categoryGearboxECU",
  "Sterowniki ABS/EBS": "catalog.categoryABSEBS",
  "Sterowniki klimatyzacji": "catalog.categoryClimate",
};

const ITEMS_PER_PAGE = 12;

type SortOption = "name" | "newest" | "popularity";

const Catalog = () => {
  const { language, t } = useLanguage();
  const [search, setSearch] = useState("");
  const [manufacturer, setManufacturer] = useState("");
  const [category, setCategory] = useState("");
  const [model, setModel] = useState("");
  const [sort, setSort] = useState<SortOption>("popularity");
  const [page, setPage] = useState(1);
  const [showFilters, setShowFilters] = useState(true);

  const availableModels = manufacturer ? models[manufacturer] || [] : Object.values(models).flat();

  const filtered = useMemo(() => {
    let result = [...products];

    if (search) {
      const q = search.toLowerCase();
      result = result.filter(
        (p) =>
          p.title.toLowerCase().includes(q) ||
          p.sku.toLowerCase().includes(q) ||
          p.otherReferences.toLowerCase().includes(q) ||
          p.model.toLowerCase().includes(q)
      );
    }
    if (manufacturer) result = result.filter((p) => p.manufacturer === manufacturer);
    if (category) result = result.filter((p) => p.category === category);
    if (model) result = result.filter((p) => p.model === model);

    switch (sort) {
      case "name":
        result.sort((a, b) => a.title.localeCompare(b.title));
        break;
      case "newest":
        result.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
        break;
      case "popularity":
        result.sort((a, b) => b.popularity - a.popularity);
        break;
    }

    return result;
  }, [search, manufacturer, category, model, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / ITEMS_PER_PAGE));
  const paginated = filtered.slice((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE);

  const resetFilters = () => {
    setManufacturer("");
    setCategory("");
    setModel("");
    setSearch("");
    setPage(1);
  };

  const selectClass =
    "h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";

  const seoTitle = t("seo.title.catalog" as any);
  const seoDescription = language === "pl"
    ? "Katalog części do samochodów ciężarowych Mercedes-Benz i MAN. Sterowniki silnika, skrzyni biegów, ABS/EBS i więcej."
    : "Auto parts catalog for Mercedes-Benz and MAN trucks. Engine ECUs, gearbox controllers, ABS/EBS and more.";

  return (
    <div className="min-h-screen bg-background">
      <SeoMetadata title={seoTitle} description={seoDescription} />
      <div className="pt-32 pb-16">
        {/* Hero */}
        <section className="bg-primary py-12 mb-8">
          <div className="container mx-auto px-4">
            <h1 className="text-3xl md:text-4xl font-bold text-primary-foreground mb-2">
              {t("catalog.pageTitle")}
            </h1>
            <h2 className="text-xl text-primary-foreground/80 font-normal">
              {t("catalog.subtitle")}
            </h2>
          </div>
        </section>

        <div className="container mx-auto px-4">
          {/* Search + filter toggle */}
          <div className="flex flex-col sm:flex-row gap-4 mb-6">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder={t("catalog.searchPlaceholder")}
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                className="pl-10"
              />
            </div>
            <Button variant="outline" onClick={() => setShowFilters(!showFilters)} className="gap-2">
              <SlidersHorizontal className="w-4 h-4" />
              {t("catalog.filters")}
            </Button>
            <div className="sm:w-48">
              <label htmlFor="catalog-sort" className="sr-only">{t("catalog.sort")}</label>
              <select id="catalog-sort" value={sort} onChange={(e) => setSort(e.target.value as SortOption)} className={selectClass}>
                <option value="popularity">{t("catalog.sortPopularity")}</option>
                <option value="newest">{t("catalog.sortNewest")}</option>
                <option value="name">{t("catalog.sortName")}</option>
              </select>
            </div>
          </div>

          {/* Filters panel */}
          {showFilters && (
            <div className="bg-secondary rounded-lg p-4 mb-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label htmlFor="catalog-manufacturer" className="text-sm font-medium text-foreground mb-1 block">{t("catalog.manufacturer")}</label>
                <select id="catalog-manufacturer" value={manufacturer} onChange={(e) => { setManufacturer(e.target.value); setModel(""); setPage(1); }} className={selectClass}>
                  <option value="">{t("catalog.all")}</option>
                  {manufacturers.map((m) => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>
              <div>
                <label htmlFor="catalog-category" className="text-sm font-medium text-foreground mb-1 block">{t("catalog.category")}</label>
                <select id="catalog-category" value={category} onChange={(e) => { setCategory(e.target.value); setPage(1); }} className={selectClass}>
                  <option value="">{t("catalog.all")}</option>
                  {categories.map((c) => <option key={c} value={c}>{categoryTranslationKeys[c] ? t(categoryTranslationKeys[c] as any) : c}</option>)}
                </select>
              </div>
              <div>
                <label htmlFor="catalog-model" className="text-sm font-medium text-foreground mb-1 block">{t("catalog.model")}</label>
                <select id="catalog-model" value={model} onChange={(e) => { setModel(e.target.value); setPage(1); }} className={selectClass}>
                  <option value="">{t("catalog.all")}</option>
                  {availableModels.map((m) => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>
              <div className="sm:col-span-3">
                <Button variant="ghost" size="sm" onClick={resetFilters}>{t("catalog.resetFilters")}</Button>
              </div>
            </div>
          )}

          {/* Results count */}
          <div className="flex justify-between items-center mb-4">
            <p className="text-sm text-muted-foreground">
              {t("catalog.resultsCount").replace("{count}", String(filtered.length))}
            </p>
          </div>

          {/* Product grid */}
          {paginated.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-8">
              {paginated.map((product) => (
                <div key={product.id} className="group bg-card border rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow flex flex-col h-full">
                  <a href={`/${language}/${routes.catalog[language]}/${product.slug}`} className="block relative aspect-[4/3] bg-secondary overflow-hidden">
                    {product.image ? (
                      <img src={product.image} alt={product.title} className="w-full h-full object-cover bg-white transition-transform duration-500 group-hover:scale-110" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-secondary/50">
                        <Package className="w-16 h-16 text-muted-foreground/30" />
                      </div>
                    )}
                    <div className="absolute top-2 left-2 bg-background/90 backdrop-blur-sm px-2 py-1 rounded text-xs font-semibold shadow-sm">
                      {product.manufacturer}
                    </div>
                  </a>
                  <div className="p-4 flex flex-col flex-grow">
                    <h3 className="font-semibold text-foreground group-hover:text-accent transition-colors text-sm leading-tight mb-2 line-clamp-2">
                      <a href={`/${language}/${routes.catalog[language]}/${product.slug}`}>
                        {product.title}
                      </a>
                    </h3>
                    <div className="text-xs text-muted-foreground space-y-1 mb-4 flex-grow">
                      <p>SKU: <span className="font-mono text-foreground">{product.sku}</span></p>
                      <p className="line-clamp-1">{[product.model, product.euro && `Euro ${product.euro}`, product.kw && `${product.kw} KW`].filter(Boolean).join(" | ")}</p>
                    </div>
                    <Button asChild size="sm" variant="outline" className="w-full mt-auto group-hover:bg-accent group-hover:text-accent-foreground group-hover:border-accent transition-colors">
                      <a href={`/${language}/${routes.catalog[language]}/${product.slug}`}>
                        {t("catalog.requestPrice")}
                      </a>
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-16 bg-muted/20 rounded-lg">
              <Package className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-medium text-foreground mb-2">{t("catalog.noResults")}</h3>
              <Button onClick={resetFilters}>{t("catalog.resetFilters")}</Button>
            </div>
          )}

          {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex justify-center gap-2">
                <Button variant="outline" size="sm" disabled={page === 1} onClick={() => setPage(page - 1)}>
                  ←
                  </Button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                  <Button key={p} variant={p === page ? "default" : "outline"} size="sm" onClick={() => setPage(p)}>
                  {p}
                  </Button>
                ))}
                <Button variant="outline" size="sm" disabled={page === totalPages} onClick={() => setPage(page + 1)}>
               →
             </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Catalog;
